import type { ComponentProps, ElementType, ReactNode } from 'react';
import { merge } from 'lodash-es';

import type { VariantProp } from '../../common/colors';
import type { BadgeProps } from '../../components/Badge/Badge';
import type { SummaryListPairsProps } from '../../components/SummaryList/SummaryList';
import type { GutterProp } from '../../utilities/shared/Gutter';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { OverflowProps } from '../Overflow/Overflow';
import { type IconName, Icon } from '../../assets/Icon/Icon';
import { selectors } from '../../common/selectors';
import { transitions } from '../../common/transitions';
import {
  getVariantStyles,
  variantBackgroundColor,
  variantBodyColor,
  variantHeadingColor,
  variantRestBodyColor,
  variantStrokeColor,
} from '../../common/variant';
import { Badge } from '../../components/Badge/Badge';
import { BadgeGroup } from '../../components/Badge/BadgeGroup';
import { SummaryList } from '../../components/SummaryList/SummaryList';
import { ControlGroup } from '../../controls/ControlGroup/ControlGroup';
import { UtilityControl } from '../../controls/UtilityControl/UtilityControl';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Small } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { gutterCSS } from '../../utilities/shared/Gutter';
import { spacingColumnCSS, spacingRowCSS } from '../../utilities/shared/Spacing';
import { HStack } from '../../utilities/Stack/HStack';
import { usePresentation } from '../../utilities/usePresentation';
import { SelectedProvider } from '../../utilities/useSelected';
import { Overflow } from '../Overflow/Overflow';

const CardIcon = styled(Icon, {
  color: variantBodyColor,
});
CardIcon.displayName = 'CardIcon';

const CardHeading = styled(Text, {
  color: variantHeadingColor,
  fontWeight: '$bold',
});
CardHeading.displayName = 'CardHeading';

// `Small` hardcodes a neutral colour that beats anything inherited, so the description has to
// read the variant local itself.
const CardDescription = styled(Small, {
  color: variantRestBodyColor,
});
CardDescription.displayName = 'CardDescription';

const CardHeader = styled(AlignStack);
CardHeader.displayName = 'CardHeader';

const CardFooterMetadata = styled(Text);
CardFooterMetadata.displayName = 'CardFooterMetadata';

const CardFooterActions = styled(ControlGroup);
CardFooterActions.displayName = 'CardFooterActions';

const CardFooter = styled(AlignStack, {
  height: '100%',
  padding: '$4 0 0',
});
CardFooter.displayName = 'CardFooter';

const CardContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$4',
  padding: '$10',
  borderRadius: '$10',
  border: '1px solid',
  borderColor: colors.strokeNeutralLight,
  transition: transitions.focusRing,

  [darkThemeSelector]: {
    borderColor: colors.strokeNeutralDark,
  },

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    isPressable: {
      true: {
        [selectors.hover]: {
          backgroundColor: colors.bgNeutralLight,
          $$summaryListSurface: colors.bgNeutralLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgNeutralDark,
            $$summaryListSurface: colors.bgNeutralDark,
          },
        },

        [selectors.focus]: {
          boxShadow: shadows.focusRingLight,

          [darkThemeSelector]: {
            boxShadow: shadows.focusRingDark,
          },
        },
      },
      false: {},
    },
    // One rule for all six variants: `resolveVariant` has already promoted a neutral card to
    // brand by the time this runs.
    selected: {
      true: {
        [`&, ${selectors.hover}`]: {
          backgroundColor: variantBackgroundColor,
          strokeAll: variantStrokeColor,
          color: variantRestBodyColor,
        },
      },
      false: {},
    },
    variant: {
      alternative: {
        ...getVariantStyles('alternative'),
      },
      attention: {
        ...getVariantStyles('attention'),
      },
      brand: {
        ...getVariantStyles('brand'),
      },
      negative: {
        ...getVariantStyles('negative'),
      },
      neutral: {
        ...getVariantStyles('neutral'),
      },
      positive: {
        ...getVariantStyles('positive'),
      },
    },
  },
});
CardContainer.displayName = 'CardContainer';

export type CardProps = {
  /**
   * The actions of the card.
   */
  actions?: ReactNode;
  /**
   * The badges of the card.
   */
  badges?: Omit<BadgeProps, 'size'>[];
  /**
   * Provide a dismiss action for the card.
   */
  dismiss?: {
    label: ReactNode;
    onClick: () => void;
  };
  /**
   * The heading of the card.
   */
  heading: ReactNode;
  /**
   * The description of the card.
   */
  description?: ReactNode;
  /**
   * The icon of the card.
   */
  icon?: IconName;
  /**
   * A unique identifier for the card.
   */
  id: string;
  /**
   * The metadata of the card.
   */
  metadata?: ReactNode;
  /**
   * The summary list pairs of the card.
   */
  pairs?: SummaryListPairsProps[];
  /**
   * Whether the card is selected.
   */
  selected?: boolean;
  /**
   * The variant of the card.
   */
  variant?: VariantProp;
  // Routing
  as?: ElementType;
  to?: string;
  onClick?: () => void;
} & ComponentProps<typeof CardContainer>;

export function Card({
  actions,
  badges,
  description,
  dismiss,
  heading,
  icon,
  id,
  metadata,
  pairs,
  selected,
  variant,
  // Routing
  as,
  to,
  onClick,
  ref,
  ...remaining
}: CardProps) {
  const hasFooter = actions || metadata;
  const isPressable = !!onClick || (!!as && !!to);
  const { selected: isSelected, resolvedVariant } = usePresentation({ variant, selected });

  return (
    <CardContainer
      isPressable={isPressable}
      selected={isSelected}
      variant={resolvedVariant}
      ref={ref}
      {...remaining}
    >
      <SelectedProvider value={isSelected}>
        <CardHeader
          gap={6}
          preset="body"
          start={icon && <CardIcon icon={icon} size={14} />}
          end={
            <HStack spacing={4}>
              {badges && (
                <BadgeGroup relation="separate" size="small">
                  {badges.map((badge) => (
                    <Badge key={badge.id} {...badge} />
                  ))}
                </BadgeGroup>
              )}
              {dismiss && (
                <UtilityControl
                  onClick={dismiss.onClick}
                  icon="cross"
                  label={dismiss.label}
                  variant="neutral"
                />
              )}
            </HStack>
          }
        >
          <CardHeading weight="bold">{heading}</CardHeading>
        </CardHeader>
        {description && <CardDescription>{description}</CardDescription>}
        {pairs && <SummaryList pairs={pairs} />}
        {hasFooter && (
          <CardFooter
            align="end"
            gap={8}
            preset="small"
            width="100%"
            end={
              actions && (
                <CardFooterActions relation="separate" size="small">
                  {actions}
                </CardFooterActions>
              )
            }
          >
            <CardFooterMetadata>{metadata}</CardFooterMetadata>
          </CardFooter>
        )}
      </SelectedProvider>
    </CardContainer>
  );
}

const CardsGrid = styled('div', {
  display: 'grid',
  gridAutoRows: '1fr',
  gridTemplateColumns: 'repeat(auto-fit, minmax(308px, 1fr))',
});
CardsGrid.displayName = 'CardsGrid';

const CardsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$8',
});
CardsContainer.displayName = 'CardsContainer';

export type CardsProps = {
  cards: CardProps[];
  gap?: SpacingScale | { row?: SpacingScale; column?: SpacingScale };
  overflow?: OverflowProps & { maximum?: number };
} & GutterProp;

export function Cards({
  cards,
  gap = 8,
  gutter = { context: 'application', edge: 'all' },
  overflow,
}: CardsProps) {
  const containerCSS = gutterCSS(gutter);
  const gridCSS = merge(
    spacingRowCSS(typeof gap === 'object' ? gap.row : gap),
    spacingColumnCSS(typeof gap === 'object' ? gap.column : gap),
  );

  const hasOverflow = overflow && overflow.maximum && cards.length > overflow?.maximum;

  return (
    <CardsContainer css={containerCSS}>
      <CardsGrid css={gridCSS}>
        {cards.slice(0, overflow?.maximum).map((card) => (
          <Card key={card.id} {...card} />
        ))}
      </CardsGrid>
      {hasOverflow && (
        <Overflow
          as={overflow.as}
          to={overflow.to}
          onClick={overflow.onClick}
          label={overflow.label}
          total={overflow.total}
        />
      )}
    </CardsContainer>
  );
}
